import { DeviceWithInstalledPower, getProductionData } from '@e2m-customer-portal/domains';
import { ForecastSlice, SpotPriceForecast, useLocale } from '@e2m-customer-portal/hooks';
import { getInputFieldTypes, getMonthName, getMonthNumber, InputFieldType } from '@e2m-customer-portal/shared';
import { addMinutes, eachMinuteOfInterval, getMonth, getTime, Interval, startOfMonth, toDate } from 'date-fns';
import { dateAxisFormatter, tooltipPositioner } from 'domains/forecast/feed-in-page/feed-in-chart/utils';
import { FOUR_HOURS_MILLISECONDS, ONE_HOUR_MILLISECONDS } from 'domains/forecast/utils';
import { ForecastProperty, ForecastTarget, Resolution } from 'generated/graphql';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsPattern from 'highcharts/modules/pattern-fill';
import { equals } from 'ramda';
import { memo, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTooltipLineChart, getAreaRangeSeries, getIntradayAdjustmentData, legendItemClick } from './utils';

export enum chartSeriesColors {
  production = '#5D8A66',
  powerFeed = '#00994C',
  intraday = '#5D8A66',
  spotPrices = '#FFB210',
  monthlyMarketValue = '#802C0B',
  afrrPlus = '#2BAEFF8D',
  afrrMinus = '#2BAEFF3D',
  mfrrPlus = '#6930BE8D',
  mfrrMinus = '#6930BE3D',
  fcr = '#FC809F8D',
}

export interface LineChartProps {
  device: DeviceWithInstalledPower;
  interval: Interval;
  slices: readonly ForecastSlice[];
  controlEnergySlices: readonly ForecastSlice[] | undefined;
  marketLocationName: string;
  spotPriceForecast?: SpotPriceForecast;
  dayAheadDeadlineDate?: Date;
  currentForecastProperty?: ForecastProperty;
  isControlEnergyForecast: boolean;
  isMobile?: boolean;
  showIntraday?: boolean;
}

export function LineChartMemo({
  device,
  interval,
  slices,
  controlEnergySlices,
  marketLocationName,
  spotPriceForecast,
  dayAheadDeadlineDate,
  isControlEnergyForecast,
  currentForecastProperty,
  isMobile,
  showIntraday,
}: Readonly<LineChartProps>): ReactElement<LineChartProps> {
  const { t } = useTranslation();
  const { code: locale } = useLocale();
  const unit = 'kW';
  const isFifteenMinutesResolution = currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES;
  const [visibleSeries, setVisibleSeries] = useState<string[]>([
    `production-${device.id}`,
    `intraday-plus-${device.id}`,
    `intraday-minus-${device.id}`,
    `spot-prices-${device.id}`,
  ]);
  const monthlyMarketValues = spotPriceForecast?.monthlyMarketValues;
  const latestHistoricalSpotPrice = spotPriceForecast?.latestHistoricalSpotPrice
    ? getTime(spotPriceForecast?.latestHistoricalSpotPrice)
    : undefined;
  const sortedSlices = [...slices].sort((a, b) => getTime(a.date) - getTime(b.date));

  const chartRef = useRef<HighchartsReact.RefObject>(null);

  HighchartsPattern(Highcharts);

  useEffect(() => {
    const bands = getInputFieldTypes(device.products, 'bands');
    setVisibleSeries((visibleSeries) =>
      isControlEnergyForecast
        ? [
            ...visibleSeries,
            ...(bands.includes(InputFieldType.AFRR_POSITIVE) || bands.includes(InputFieldType.AFRR_NEGATIVE)
              ? [`afrrPlus-${device.id}`, `afrrMinus-${device.id}`]
              : [`fcr-${device.id}`]),
          ]
        : visibleSeries.filter(
            (series) =>
              !(
                series.includes('fcr') ||
                series.includes('afrrPlus') ||
                series.includes('afrrMinus') ||
                series.includes('mfrrPlus') ||
                series.includes('mfrrMinus')
              ),
          ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlEnergyForecast]);

  useEffect(() => {
    if (isMobile) {
      setVisibleSeries((visibleSeries) =>
        isControlEnergyForecast
          ? [
              ...visibleSeries,
              `afrrPlus-${device.id}`,
              `afrrMinus-${device.id}`,
              `fcr-${device.id}`,
              `mfrrPlus-${device.id}`,
              `mfrrMinus-${device.id}`,
            ]
          : [`production-${device.id}`, `spot-prices-${device.id}`, `monthly-market-value-${device.id}`],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isControlEnergyForecast]);

  const options: Highcharts.Options = {
    chart: {
      height: isMobile ? 200 : 210,
      marginLeft: isMobile ? undefined : 193,
      marginRight: isMobile ? undefined : 31,
      marginTop: isMobile ? 20 : 45,
      marginBottom: isMobile ? undefined : 48,
      scrollablePlotArea: isMobile ? { minWidth: 2000, scrollPositionX: 0 } : undefined,
    },
    legend: {
      useHTML: true,
      enabled: !isMobile,
      layout: 'horizontal',
      align: 'right',
      verticalAlign: 'top',
      x: -10,
      y: -15,
      maxHeight: 50,
      itemStyle: {
        textOverflow: 'visible',
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: legendItemClick(setVisibleSeries),
        },
      },
    },
    tooltip: {
      useHTML: true,
      shared: true,
      outside: true,
      style: { zIndex: 1300 },
      formatter: formatTooltipLineChart(
        t,
        locale,
        marketLocationName,
        device.name,
        toDate(interval.end).getTime(),
        latestHistoricalSpotPrice,
      ),
      positioner: tooltipPositioner,
    },
    yAxis: [
      {
        id: 'production',
        gridLineWidth: 1,
        labels: {
          align: 'right',
          style: {
            color: chartSeriesColors.production,
          },
          x: -10,
        },
        title: {
          text: visibleSeries.includes(`production-${device.id}`)
            ? `${t('SHARED_PRODUCT_PRODUCTION')} (${unit})`
            : undefined,
          style: { color: chartSeriesColors.production },
        },
        visible: !!visibleSeries.filter(
          (series) => !(series.includes('spot-prices') || series.includes('monthly-market-value')),
        ).length,
      },
      {
        id: 'prices',
        gridLineWidth: 1,
        labels: {
          align: 'right',
          style: {
            color: chartSeriesColors.spotPrices,
          },
          x: -5,
          y: -5,
        },
        title: {
          text:
            visibleSeries.includes(`spot-prices-${device.id}`) ||
            visibleSeries.includes(`monthly-market-value-${device.id}`)
              ? `${t('SPOT_PRICES')} (€/MWh)`
              : undefined,
          style: { color: chartSeriesColors.spotPrices },
        },
        opposite: true,
        visible: !!visibleSeries.filter(
          (series) => series.includes('spot-prices') || series.includes('monthly-market-value'),
        ).length,
      },
    ],
    xAxis: [
      {
        type: 'datetime',
        max: getTime(interval.end),
        min: getTime(interval.start),
        gridLineWidth: 1,
        gridLineDashStyle: 'Dash',
        startOnTick: true,
        minPadding: 0,
        endOnTick: true,
        maxPadding: 0,
        tickInterval: isFifteenMinutesResolution ? ONE_HOUR_MILLISECONDS : FOUR_HOURS_MILLISECONDS,
        labels: {
          useHTML: true,
          formatter: dateAxisFormatter(t),
        },
      },
      {
        type: 'datetime',
        gridLineWidth: 1,
        gridLineColor: '#C8CBCE',
        tickInterval: FOUR_HOURS_MILLISECONDS * 6,
        linkedTo: 0,
        offset: 0,
        labels: {
          enabled: false,
        },
      },
    ],
    series: [
      {
        type: 'line',
        id: `production-${device.id}`,
        visible: isMobile || visibleSeries.includes(`production-${device.id}`),
        step: 'left',
        name: t('SHARED_PRODUCT_PRODUCTION'),
        color: chartSeriesColors.production,
        marker: { enabled: true, radius: 1, symbol: 'circle' },
        softThreshold: true,
        turboThreshold: 0,
        data: getProductionData(sortedSlices, toDate(interval.end).getTime()),
        yAxis: 'production',
        zIndex: 2,
      },
      ...(showIntraday && currentForecastProperty?.target === ForecastTarget.VPP
        ? [
            {
              type: 'arearange',
              id: `intraday-plus-${device.id}`,
              visible: isMobile || visibleSeries.includes(`intraday-plus-${device.id}`),
              step: 'left',
              stickyTracking: false,
              lineWidth: 0,
              color: `${chartSeriesColors.intraday}8D`,
              data: getIntradayAdjustmentData(
                sortedSlices,
                (dayAheadDeadlineDate ?? toDate(interval.end)).getTime(),
              ).filter((value) => !value.y || (value.y && value.y > 0)),
              name: t('SHARED_PRODUCT_INTRADAY_ADJUSTMENT_PLUS'),
              marker: { enabled: false },
              states: { hover: { enabled: false } },
              zIndex: 1,
            } satisfies Highcharts.SeriesOptionsType,
            {
              type: 'arearange',
              id: `intraday-minus-${device.id}`,
              visible: isMobile || visibleSeries.includes(`intraday-minus-${device.id}`),
              step: 'left',
              stickyTracking: false,
              lineWidth: 0,
              color: {
                pattern: {
                  color: `${chartSeriesColors.intraday}6D`,
                  backgroundColor: `${chartSeriesColors.intraday}4D`,
                  path: {
                    d: 'M 0 0 L 5 5 M 4 -1 L 6 1 M -1 4 L 1 6',
                  },
                  width: 5,
                  height: 5,
                },
              },
              data: getIntradayAdjustmentData(
                sortedSlices,
                (dayAheadDeadlineDate ?? toDate(interval.end)).getTime(),
              ).filter((value) => !value.y || (value.y && value.y < 0)),
              name: t('SHARED_PRODUCT_INTRADAY_ADJUSTMENT_MINUS'),
              marker: { enabled: false },
              states: { hover: { enabled: false } },
              zIndex: 1,
            } satisfies Highcharts.SeriesOptionsType,
          ]
        : []),
      {
        type: 'line',
        id: `spot-prices-${device.id}`,
        visible: isMobile || visibleSeries.includes(`spot-prices-${device.id}`),
        step: 'left',
        name: t('SPOT_PRICES'),
        color: chartSeriesColors.spotPrices,
        zoneAxis: 'x',
        zones: [{ value: latestHistoricalSpotPrice ?? toDate(interval.start).getTime() }, { dashStyle: 'ShortDot' }],
        marker: { enabled: false },
        softThreshold: true,
        turboThreshold: 0,
        // Quadruple the spot price slice so every quarter hour has its own datapoint
        data: spotPriceForecast?.dayAheadForecasts?.flatMap((slice) =>
          isFifteenMinutesResolution
            ? [
                [getTime(slice.date), slice.price],
                [getTime(addMinutes(slice.date, 15)), slice.price],
                [getTime(addMinutes(slice.date, 30)), slice.price],
                [getTime(addMinutes(slice.date, 45)), slice.price],
              ]
            : [[getTime(slice.date), slice.price]],
        ),
        yAxis: 'prices',
      },
      {
        type: 'line',
        id: `monthly-market-value-${device.id}`,
        visible: isMobile || visibleSeries.includes(`monthly-market-value-${device.id}`),
        step: 'left',
        name: t('SPOT_PRICES_MONTHLY_MARKET_VALUE', { month: null }),
        color: chartSeriesColors.monthlyMarketValue,
        zoneAxis: 'x',
        zones: [
          {
            value: getTime(
              startOfMonth(new Date()).setMonth(
                getMonthNumber(
                  spotPriceForecast?.monthlyMarketValues.find((value) => value.isProvisional)?.month ??
                    getMonthName(new Date().getMonth()),
                ),
              ),
            ),
          },
          { dashStyle: 'ShortDot' },
        ],
        marker: { enabled: false },
        softThreshold: true,
        turboThreshold: 0,
        data: eachMinuteOfInterval(
          { start: interval.start, end: interval.end },
          { step: isFifteenMinutesResolution ? 15 : 60 },
        ).map((hour) => {
          const currentMonthValue = monthlyMarketValues?.find(
            (value) => getMonth(hour) === getMonthNumber(value.month),
          );
          return [getTime(hour), currentMonthValue?.mean];
        }),
        yAxis: 'prices',
      },
      ...(controlEnergySlices
        ? getAreaRangeSeries(controlEnergySlices, sortedSlices, device, visibleSeries, toDate(interval.end).getTime())
        : []),
    ],
  };

  return (
    <>
      {!isMobile && <p className="mr-7 mt-1 text-right text-xs">{t('CHART_LEGEND_FORECAST')}</p>}
      <HighchartsReact options={options} highcharts={Highcharts} ref={chartRef} />
    </>
  );
}

export const LineChart = memo(LineChartMemo, equals);
